import React from 'react';
import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { colors } from '@src/styles';

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  startDateLabel?: string;
  endDateLabel?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startDateLabel = 'Start Date',
  endDateLabel = 'End Date',
}) => {
  const handleStartDateChange = (date: Date | null) => {
    if (date && endDate && date > endDate) onEndDateChange(null);
    onStartDateChange(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date && startDate && startDate > date) onStartDateChange(null);
    onEndDateChange(date);
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'red',
      },

      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primary.yellow,
      },

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primary.yellow,
      },
    },

    '& input:-webkit-autofill': {
      WebkitTextFillColor: `${colors.primary.white} !important`,
      transition: 'background-color 5000s ease-in-out 0s',
    },
  } as const;

  const popperSx = {
    '& .MuiPaper-root': {
      backgroundColor: colors.background.primary,
      color: colors.primary.white,
    },

    '& .MuiPickersCalendarHeader-root': {
      color: colors.primary.white,
    },

    '& .MuiPickersCalendarHeader-label': {
      color: colors.primary.white,
    },

    '& .MuiPickersCalendarHeader-switchViewButton': {
      color: colors.primary.white,
      '&:hover': { backgroundColor: colors.background.secondary },
    },

    '& .MuiPickersArrowSwitcher-button': {
      color: colors.primary.white,
      '&:hover': { backgroundColor: colors.background.secondary },
    },

    '& .MuiDayCalendar-weekDayLabel': {
      color: colors.primary.white,
    },

    '& .MuiPickersDay-root': {
      color: colors.primary.white,
      backgroundColor: 'transparent',
      '&:hover': { backgroundColor: colors.background.secondary },
      '&.Mui-selected': {
        backgroundColor: colors.primary.yellow,
        color: colors.background.primary,
        '&:hover': { backgroundColor: colors.primary.yellow },
      },
      '&.MuiPickersDay-today': {
        borderColor: colors.primary.yellow,
        color: colors.primary.yellow,
      },
    },

    '& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': {
      color: colors.primary.white,
      '&:hover': { backgroundColor: colors.background.secondary },
      '&.Mui-selected': {
        backgroundColor: colors.primary.yellow,
        color: colors.background.primary,
        '&:hover': { backgroundColor: colors.primary.yellow },
      },
    },

    '& .MuiYearCalendar-root': {
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  } as const;

  const commonSlotProps = {
    textField: { size: 'small', sx: textFieldSx },
    popper: { sx: popperSx },
  } as const;

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <DatePicker
        label={startDateLabel}
        value={startDate}
        onChange={handleStartDateChange}
        format="dd/MM/yyyy"
        slotProps={commonSlotProps}
      />

      <Typography variant="body2" sx={{ color: colors.primary.white }}>
        to
      </Typography>

      <DatePicker
        label={endDateLabel}
        value={endDate}
        onChange={handleEndDateChange}
        format="dd/MM/yyyy"
        slotProps={commonSlotProps}
      />
    </Box>
  );
};
